import { t as translate } from "./preferences";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
  type ReactNode,
} from "react";
import { api, ApiError, getToken, setToken, observeAccess, type UserOut, type TariffStatusOut } from "./api";
import { useAppEnvironment } from "../telegram";
import { clearWorkDraftUnlessPending, markWorkHistoryBoundary } from "./workDraft";
import { clearConfirmations } from "./confirmationState";
import { clearWarehouseOperationUnlessPending, readWarehouseOperation } from "./warehouseOperation";
import { clearReturnDraftsForUser } from "./returnDraft";

type AuthErrorState = {
  status: "error";
  error: ApiError;
  retry: () => void;
};

export type AuthState =
  | { status: "booting" }
  | { status: "unauthenticated" }
  | { status: "authenticated"; user: UserOut }
  | AuthErrorState;

type AuthContextValue = {
  state: AuthState;
  access?: { checking: boolean; recovery: boolean; error: ApiError | null; tariff: TariffStatusOut | null; revision: number };
  refresh?: () => Promise<void>;
  login: (identifier: string, password: string) => Promise<void>;
  changePassword: (
    currentPassword: string,
    newPassword: string,
    confirmPassword: string,
  ) => Promise<void>;
  logout: () => Promise<void>;
  updateUser: (user: UserOut) => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function isMechanicAppUser(user: UserOut): boolean {
  return user.role === "workshop_owner" || user.role === "employee";
}

export function hasAnyBusinessCapability(user: UserOut): boolean {
  return (
    user.role === "workshop_owner" ||
    Boolean(user.can_be_usta || user.can_be_sotuvchi || user.can_be_omborchi)
  );
}

export function hasCapability(
  user: UserOut,
  capability: "usta" | "warehouse",
): boolean {
  if (user.role === "workshop_owner") return true;
  if (capability === "usta") return Boolean(user.can_be_usta);
  return Boolean(user.can_be_sotuvchi || user.can_be_omborchi);
}

export function canSell(user: UserOut): boolean {
  return user.role === "workshop_owner" || Boolean(user.can_be_sotuvchi);
}

export function canManageStock(user: UserOut): boolean {
  return user.role === "workshop_owner" || Boolean(user.can_be_omborchi);
}

function normalizeError(error: unknown): ApiError {
  return error instanceof ApiError
    ? error
    : new ApiError("unknown_error", translate("Kutilmagan xatolik yuz berdi"), 0);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: "booting" });
  const [access, setAccess] = useState({ checking: false, recovery: false, error: null as ApiError | null, tariff: null as TariffStatusOut | null, revision: 0 });
  const stateRef = useRef(state); stateRef.current = state;
  const accessRef = useRef(access); accessRef.current = access;
  const refreshing = useRef<Promise<void> | null>(null);
  const readController = useRef<AbortController | null>(null);
  const { isActive } = useAppEnvironment();
  const activeRef = useRef(isActive); activeRef.current = isActive;
  const generation = useRef(0);
  const mounted = useRef(false);

  const refresh = useCallback((): Promise<void> => {
    if (refreshing.current) return refreshing.current;
    if (!getToken() || stateRef.current.status !== "authenticated") return Promise.resolve();
    const request = generation.current;
    const controller = new AbortController();
    readController.current = controller;
    const timeout = window.setTimeout(() => controller.abort(), 15_000);
    accessRef.current = { ...accessRef.current, checking: true };
    setAccess(accessRef.current);
    let task: Promise<void>;
    task = (async () => {
      try {
        const user = await api.me(controller.signal);
        if (!mounted.current || request !== generation.current) return;
        const previous = stateRef.current;
        if (!isMechanicAppUser(user) || previous.status !== "authenticated" || previous.user.id !== user.id || previous.user.workshop_id !== user.workshop_id) {
          setToken(null); setState({ status: "unauthenticated" }); return;
        }
        // Apply revocation/masking before waiting for tariff or other business reads.
        setState({ status: "authenticated", user });
        const tariff = user.must_change_password ? null : await api.tariff(controller.signal);
        if (!mounted.current || request !== generation.current) return;
        setAccess((old) => ({ checking: false, recovery: false, error: null, tariff, revision: old.revision + 1 }));
      } catch (caught) {
        if (!mounted.current || request !== generation.current) return;
        const error = normalizeError(caught);
        setAccess((old) => ({ ...old, checking: false, recovery: error.status === 401, error }));
      } finally {
        window.clearTimeout(timeout);
        if (request === generation.current) refreshing.current = null;
      }
    })();
    refreshing.current = task;
    return task;
  }, []);

  useEffect(() => observeAccess((error) => {
    if (stateRef.current.status !== "authenticated") return;
    if (error.status === 401 && error.code !== "password_change_required") {
      accessRef.current = { ...accessRef.current, recovery: true, error };
      setAccess(accessRef.current);
    } else { void refresh(); }
  }, () => activeRef.current && !accessRef.current.checking && !accessRef.current.recovery && !accessRef.current.error), [refresh]);

  useEffect(() => {
    if (state.status !== "authenticated" || !isActive) return;
    void refresh();
    const run = () => { if (document.visibilityState !== "hidden") void refresh(); };
    const timer = window.setInterval(run, 60_000);
    window.addEventListener("focus", run);
    window.addEventListener("pageshow", run);
    window.addEventListener("online", run);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", run);
      window.removeEventListener("pageshow", run);
      window.removeEventListener("online", run);
    };
  }, [state.status, isActive, refresh]);

  const restore = useCallback(async () => {
    const request = ++generation.current;
    if (!getToken()) {
      setState({ status: "unauthenticated" });
      return;
    }
    setState({ status: "booting" });
    try {
      const user = await api.me();
      if (!mounted.current || request !== generation.current) return;
      if (!isMechanicAppUser(user)) {
        setToken(null);
        setState({ status: "unauthenticated" });
        return;
      }
      setState({ status: "authenticated", user });
    } catch (error) {
      if (!mounted.current || request !== generation.current) return;
      const normalized = normalizeError(error);
      if (normalized.status === 401) {
        setToken(null);
        setState({ status: "unauthenticated" });
        return;
      }
      setState({ status: "error", error: normalized, retry: restore });
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    void restore();
    return () => { mounted.current = false; ++generation.current; readController.current?.abort(); };
  }, [restore]);

  const login = useCallback(async (identifier: string, password: string) => {
    const request = ++generation.current;
    const result = await api.login(identifier, password);
    if (!mounted.current || request !== generation.current) {
      throw new ApiError("request_cancelled", translate("So‘rov bekor qilindi"), 0);
    }
    setToken(result.access_token);
    if (!isMechanicAppUser(result.user)) {
      try {
        await api.logout();
      } catch {
        // The token is never retained for unsupported application roles.
      } finally {
        if (getToken() === result.access_token) setToken(null);
      }
      throw new ApiError(
        "unsupported_role",
        translate("Bu ilova faqat ustaxona egasi va xodimlari uchun"),
        403,
      );
    }
    const previous = stateRef.current;
    if (previous.status !== "authenticated" || previous.user.id !== result.user.id || previous.user.workshop_id !== result.user.workshop_id) {
      clearConfirmations();
      markWorkHistoryBoundary();
      if (previous.status === "authenticated") {
        clearWorkDraftUnlessPending(previous.user.id);
        if (previous.user.workshop_id !== null) clearReturnDraftsForUser(previous.user.id, previous.user.workshop_id,
          readWarehouseOperation(previous.user.id, previous.user.workshop_id)?.operationUuid);
      }
    }
    setState({ status: "authenticated", user: result.user });
    stateRef.current = { status: "authenticated", user: result.user };
    setAccess((old) => ({ checking: false, recovery: false, error: null, tariff: null, revision: old.revision + 1 }));
    refreshing.current = null;
    await refresh();
  }, [refresh]);

  const changePassword = useCallback(
    async (
      currentPassword: string,
      newPassword: string,
      confirmPassword: string,
    ) => {
      const request = generation.current;
      try {
        const user = await api.changePassword(
          currentPassword,
          newPassword,
          confirmPassword,
        );
        if (!mounted.current || request !== generation.current) {
          throw new ApiError("request_cancelled", translate("So‘rov bekor qilindi"), 0);
        }
        setState({ status: "authenticated", user });
      } catch (error) {
        const normalized = normalizeError(error);
        if (mounted.current && request === generation.current && normalized.status === 401) {
          setAccess((old) => ({ ...old, recovery: true, error: normalized }));
        }
        throw normalized;
      }
    },
    [],
  );

  const logout = useCallback(async () => {
    const credential = getToken();
    const userId = state.status === "authenticated" ? state.user.id : null;
    await api.logout();
    if (getToken() === credential) setToken(null);
    clearConfirmations();
    markWorkHistoryBoundary();
    if (userId !== null) {
      clearWorkDraftUnlessPending(userId);
      if (state.status === "authenticated" && state.user.workshop_id !== null) clearReturnDraftsForUser(userId, state.user.workshop_id,
        readWarehouseOperation(userId, state.user.workshop_id)?.operationUuid);
      clearWarehouseOperationUnlessPending(userId);
    }
    if (!mounted.current) throw new ApiError("request_cancelled", translate("So‘rov bekor qilindi"), 0);
    setState({ status: "unauthenticated" });
    ++generation.current;
    setAccess({ checking: false, recovery: false, error: null, tariff: null, revision: 0 });
  }, [state]);

  const updateUser = useCallback((user: UserOut) => {
    if (!isMechanicAppUser(user)) return;
    setState({ status: "authenticated", user });
  }, []);

  const value = useMemo(
    () => ({ state, access, refresh, login, changePassword, logout, updateUser }),
    [state, access, refresh, login, changePassword, logout, updateUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside AuthProvider");
  return context;
}

export function useAccessRevision() {
  const context = useContext(AuthContext);
  return context?.access?.revision ?? 0;
}

export function useReadsAllowed() {
  const context = useContext(AuthContext);
  return !context || (context.state.status === "authenticated" && !context.state.user.must_change_password && !context.access?.recovery);
}
